import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'reactstrap'

import ReklaimModal, { ModalHeader, ModalBody, ModalTitle } from 'components/ReklaimModal'
import { STRING } from 'resources'
import { getDataBrokerRemovalContract } from 'services/reklaimProtect'

import EmailAddressAddModal from './EmailAddressAddModal'
import RemoveEmailAddressModal from './RemoveEmailAddressModal'
import EmailAddressesSection from './EmailAddressesSection'
import UserProfileSection from './UserProfileSection'
import DataBrokerRemovalContract from './DataBrokerRemovalContract'
import { OPEN_MODAL_KEYS } from './constants'
import { ViewLayer, SpinnerContainer, ErrorMessage } from './styles'

const ProfileInfoEditModal = ({
  isOpen,
  toggle,
  profileInfo,
  setProfileInfo,
  isProfileInfoLoading,
  subscriptionStatus,
}) => {
  const [openModalKey, setOpenModalKey] = useState(null)
  const [selectedEmailAddress, setSelectedEmailAddress] = useState(null)
  const [isEmailAddressAddModalOpenedByUser, setIsEmailAddressAddModalOpenedByUser] = useState(true)
  const [emailAddressData, setEmailAddressData] = useState(profileInfo?.emailAddresses || [])
  const [userProfileData, setUserProfileData] = useState(profileInfo?.userProfile || {})
  const [contractInfo, setContractInfo] = useState(null)
  const [isContractInfoLoading, setIsContractInfoLoading] = useState(true)

  const hasCompletedProfile = useMemo(() => {
    if (!profileInfo?.userProfile || profileInfo?.userProfile?.error) return false

    return Object.entries(profileInfo.userProfile).every(([key, value]) => {
      if (key === 'address2') return true
      return value.trim() !== '' && value !== null && value !== undefined
    })
  }, [profileInfo])

  const [errorMessage, setErrorMessage] = useState(() => {
    if (
      ((profileInfo?.userProfile === null || profileInfo?.emailAddresses === null) && !isProfileInfoLoading) ||
      profileInfo?.error
    ) {
      return STRING.protect.profileInfo.edit.error.noData
    }
    return null
  })

  /* eslint-disable no-await-in-loop */
  const withRetry = async (fn, maxAttempts = 3, baseDelayMs = 1000) => {
    for (let attempts = 0; attempts < maxAttempts; attempts += 1) {
      try {
        const response = await fn()
        if (!response) {
          return null
        }

        if (response?.signedAt) {
          return response
        }
      } catch {
        return null
      }

      const delay = baseDelayMs * 2 ** attempts
      await new Promise((resolve) => {
        setTimeout(resolve, delay)
      })
    }
    return null
  }
  /* eslint-disable no-await-in-loop */

  useEffect(() => {
    const getContractInfo = async () => {
      setIsContractInfoLoading(true)
      const localContractInfo = await withRetry(getDataBrokerRemovalContract)
      setContractInfo(localContractInfo)
      setIsContractInfoLoading(false)
    }
    getContractInfo()
  }, [])

  useEffect(() => {
    if (profileInfo?.userProfile && profileInfo?.emailAddresses && !isProfileInfoLoading) {
      setUserProfileData(profileInfo.userProfile)
      setEmailAddressData(profileInfo.emailAddresses)
      setErrorMessage(null)
    }
  }, [profileInfo, isProfileInfoLoading])

  const handleModalClose = useCallback(() => {
    setOpenModalKey(null)
    setSelectedEmailAddress(null)
    setIsEmailAddressAddModalOpenedByUser(true)
  }, [setOpenModalKey, setSelectedEmailAddress, setIsEmailAddressAddModalOpenedByUser])

  return (
    <>
      <EmailAddressAddModal
        isOpen={openModalKey === OPEN_MODAL_KEYS.ADD_EMAIL_ADDRESS}
        closeModal={() => handleModalClose()}
        openModal={() => setOpenModalKey(OPEN_MODAL_KEYS.ADD_EMAIL_ADDRESS)}
        emailAddressData={emailAddressData}
        setEmailAddressData={setEmailAddressData}
        isEmailAddressAddModalOpenedByUser={isEmailAddressAddModalOpenedByUser}
        setIsEmailAddressAddModalOpenedByUser={setIsEmailAddressAddModalOpenedByUser}
        subscriptionStatus={subscriptionStatus}
      />
      <RemoveEmailAddressModal
        isModalOpen={openModalKey === OPEN_MODAL_KEYS.DELETE_EMAIL_ADDRESS}
        closeModal={() => handleModalClose()}
        emailAddress={selectedEmailAddress}
        setEmailAddressData={setEmailAddressData}
      />
      <ViewLayer $zIndex={1000}>
        <ReklaimModal isOpen={isOpen} toggle={toggle} centered>
          <ModalHeader toggle={toggle} />
          <ModalBody>
            <ModalTitle>{STRING.protect.profileInfo.edit.title}</ModalTitle>
            {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            {isProfileInfoLoading ? (
              <SpinnerContainer>
                <Spinner />
              </SpinnerContainer>
            ) : (
              <>
                <EmailAddressesSection
                  emailAddresses={emailAddressData}
                  setOpenModalKey={setOpenModalKey}
                  setEmailAddressData={setEmailAddressData}
                  selectedEmailAddress={selectedEmailAddress}
                  setSelectedEmailAddress={setSelectedEmailAddress}
                />
                <UserProfileSection
                  userProfileData={userProfileData}
                  setUserProfileData={setUserProfileData}
                  setErrorMessage={setErrorMessage}
                  setProfileInfo={setProfileInfo}
                  toggle={toggle}
                />
                {subscriptionStatus === 'subscriber' && (
                  <DataBrokerRemovalContract
                    hasCompletedProfile={hasCompletedProfile}
                    contractInfo={contractInfo}
                    setContractInfo={setContractInfo}
                    isContractInfoLoading={isContractInfoLoading}
                  />
                )}
              </>
            )}
          </ModalBody>
        </ReklaimModal>
      </ViewLayer>
    </>
  )
}

ProfileInfoEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  profileInfo: PropTypes.shape({
    userProfile: PropTypes.oneOf([
      PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        address: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
        birthDate: PropTypes.string,
      }),
      PropTypes.shape({
        error: PropTypes.bool,
      }),
    ]),
    emailAddresses: PropTypes.arrayOf(
      PropTypes.shape({
        emailAddress: PropTypes.string,
        isPrimary: PropTypes.bool,
      }),
    ),
    error: PropTypes.bool,
  }),
  setProfileInfo: PropTypes.func.isRequired,
  isProfileInfoLoading: PropTypes.bool.isRequired,
  subscriptionStatus: PropTypes.string,
}

export default ProfileInfoEditModal
